import classNames from 'classnames';
import type { AlertProps, AlertType } from './AlertV2.types';

type AlertStyles = {
  wrapper: string;
  icon: string;
  title: string;
  subtitle: string;
  primaryAction: string;
  divider: string;
  secondaryAction: string;
  closeButton: string;
};

// TODO: some colors are currently hardcoded 'cos they're not in the themeColors.json yet
const AlertV2FilledColorMap: Record<AlertType, AlertStyles> = {
  error: {
    wrapper: 'relative bg-[#FEF2F2] border border-[#FEE2E2]',
    icon: 'text-danger-dark',
    title: 'text-danger-dark',
    subtitle: 'text-danger-dark',
    primaryAction: 'text-[#DC2626]',
    divider: 'text-[#F87171]',
    secondaryAction: 'text-[#F87171]',
    closeButton: 'text-[#F87171]',
  },
  warning: {
    wrapper: 'bg-[#FFFBEB] border border-[#FEF3C7]',
    icon: 'text-[#78350F]',
    title: 'text-[#78350F]',
    subtitle: 'text-[#78350F]',
    primaryAction: 'text-[#B45309]',
    divider: 'text-[#FCD34D]',
    secondaryAction: 'text-[#D97706]',
    closeButton: 'text-[#F59E0B]',
  },
  success: {
    wrapper: 'bg-[#ECFDF5] border border-[#D1FAE5]',
    icon: 'text-success-dark',
    title: 'text-success-dark',
    subtitle: 'text-success-dark',
    primaryAction: 'text-[#059669]',
    divider: 'text-[#34D399]',
    secondaryAction: 'text-[#34D399]',
    closeButton: 'text-green-400',
  },
  information: {
    wrapper: 'bg-[#EFF6FF] border border-[#DBEAFE]',
    icon: 'text-[#1E3A8A]',
    title: 'text-[#1E3A8A]',
    subtitle: 'text-[#1E3A8A]',
    primaryAction: 'text-[#2563EB]',
    divider: 'text-[#60A5FA]',
    secondaryAction: 'text-[#60A5FA]',
    closeButton: 'text-[#60A5FA]',
  },
  neutre: {
    wrapper: 'bg-primary-25 border border-[#E7E7E7]',
    icon: 'text-primary-950',
    title: 'text-primary-950',
    subtitle: 'text-primary-600',
    primaryAction: 'text-primary-950',
    divider: 'text-primary-600',
    secondaryAction: 'text-primary-600',
    closeButton: 'text-primary-400',
  },
  primary: {
    wrapper: 'bg-taxes-50 border border-[#DDD6F9]',
    icon: 'text-taxes-950',
    title: 'text-taxes-950',
    subtitle: 'text-taxes-950',
    primaryAction: 'text-taxes-500',
    divider: 'text-taxes-300',
    secondaryAction: 'text-taxes-300',
    closeButton: 'text-taxes-300',
  },
  ai: {
    wrapper: 'bg-taxes-50 border border-[#DDD6F9]',
    icon: 'text-taxes-500',
    title: 'text-taxes-950',
    subtitle: 'text-taxes-950',
    primaryAction: 'text-taxes-500',
    divider: 'text-taxes-300',
    secondaryAction: 'text-taxes-300',
    closeButton: 'text-taxes-300',
  },
};

export const AlertV2PaddingMap: Record<AlertProps['size'], string> = {
  'x-small': 'p-2',
  small: 'py-2 px-3',
  'large-mid': 'p-[10px] pb-3',
  large: 'px-4 pt-[14px] pb-4',
};

export const AlertV2TextSizeMap: Record<AlertProps['size'], string> = {
  'x-small': 'text-xs',
  small: 'text-sm',
  'large-mid': 'text-sm',
  large: 'text-sm',
};

export const useAlertStyles = (
  props: Pick<AlertProps, 'type' | 'size' | 'variant'>,
): AlertStyles => {
  const colors = AlertV2FilledColorMap[props.type];
  const padding = AlertV2PaddingMap[props.size];
  const textSize = AlertV2TextSizeMap[props.size];
  const isFilled = props.variant === 'filled';

  return {
    // wrapper styles
    wrapper: classNames(
      'rounded-lg backdrop-blur-sm inline-flex gap-2 items-start',
      padding,
      isFilled ? colors.wrapper : 'bg-white border border-[#E7E7E7]',
    ),
    // content styles
    icon: classNames('w-6 h-6 flex-shrink-0', colors.icon),
    title: classNames(
      'font-semibold',
      textSize,
      isFilled ? colors.title : 'text-gray-900',
    ),
    subtitle: classNames(
      textSize,
      isFilled ? colors.subtitle : 'text-gray-600',
    ),
    // close button styles
    closeButton: classNames(
      'hover:opacity-50 transition-opacity duration-200',
      isFilled ? colors.closeButton : 'text-primary-400',
    ),
    // actions styles
    primaryAction: classNames(
      'underline font-semibold',
      textSize,
      isFilled ? colors.primaryAction : 'text-primary',
    ),
    divider: classNames(
      textSize,
      isFilled ? colors.divider : 'text-primary-600',
    ),
    secondaryAction: classNames(
      'underline font-semibold',
      textSize,
      isFilled ? colors.secondaryAction : 'text-primary-600',
    ),
  };
};
